import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, interval } from 'rxjs';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import {
  LIVE_SUPPORT_TAKEN_BY,
  LiveSupportSessionCard,
  demoChipTooltip,
  isLaterSessionOfTicket
} from '@features/xatbot/models/xat-bot-live-support.model';
import { LiveSupportService } from '@features/xatbot/services/live-support.service';
import { TellMe } from '@shared/models/TellMe';
import { CollaborationState } from '../../store/collaboration.reducer';
import { Store } from '@ngrx/store';
import { getConversationList } from '../../store/collaboration.selector';
import {
  GetConversationChatAction, GetConversationListAction,
  PickupLiveSupportConversationAction
} from '../../store/collaboration.action';
import { CollaborationConfig } from '../../store/collaboration.config';
import { CollaborationQuery } from '../../store/collaboration.query';
import { PlatformSidebarState } from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import { SetCurrentRightSideComponentAction } from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import { ConversationComponentName, NewConversationComponentName } from '../../store/collaboration.constants';
import { GraphqlContentScope } from '@shared/models/graphql-content-scope.model';
import { ContentScope } from '@shared/commands/command-base';
import { ViewSynchronizerService } from '@shared/components/platform-sidebar/store/view-synchronizer.service';
import { PickupLiveSupportConversationCommand } from '../../store/collaboration.command';

@Component({
    standalone: false,
    selector: 'particle-waiting-support-conversation-list',
  templateUrl: './waiting-support-conversation-list.component.html',
  styleUrls: ['./waiting-support-conversation-list.component.scss']
})
export class WaitingSupportConversationListComponent implements OnInit, OnDestroy {
  static readonly LiveRequestsRefreshMs = 10000;
  /** ITP-E-177-03 — how long a request someone else answered stays on the list, saying who. */
  static readonly TakenShownMs = 8000;

  conversationList$: any;
  conversationList: any;

  /** ITP-E-177-01 — the live support requests nobody has answered yet, oldest first (support members only). */
  liveRequests: LiveSupportSessionCard[] = [];
  answering: { [sessionId: string]: boolean } = {};
  answerErrors: { [sessionId: string]: string } = {};
  /** The request this member answered: its chat opens here. */
  answered: LiveSupportSessionCard = null;
  /** ITP-E-177-03 — requests another member answered first, by session id, with when it was seen (the row says who). */
  taken: { [sessionId: string]: { request: LiveSupportSessionCard; at: number } } = {};
  readonly myEmail = TellMe.userInfo?.email;
  disconnecting = false;

  private readonly destroy$ = new Subject<void>();

  constructor (private collaborationStore: Store<CollaborationState>,
    private platformSidebarStore: Store<PlatformSidebarState>,
    private viewSynchronizerService: ViewSynchronizerService,
    private liveSupportService: LiveSupportService
) {
  }

  ngOnInit () {
    this.getConversationList();
    this.subscribeToConversationList();
    this.followLiveRequests();
  }

  ngOnDestroy () {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * ITP-E-177-01 — answers a live support request: Unanswered → Live on the server, where the first answer wins; anyone
   * later is told who has it. The winner's chat opens below the list.
   */
  answer (session: LiveSupportSessionCard) {
    if (!this.canAnswer(session)) {
      return;
    }
    this.answering[session.sessionId] = true;
    this.answerErrors[session.sessionId] = null;
    this.liveSupportService.answer(session.sessionId).subscribe(result => {
      this.answering[session.sessionId] = false;
      if (result.error) {
        // ITP-E-177-03: "Answered by Dana Smith just now." — the row stays a moment, its Answer off, then leaves.
        if (result.code === LIVE_SUPPORT_TAKEN_BY) {
          this.taken[session.sessionId] = { request: session, at: Date.now() };
        }
        this.answerErrors[session.sessionId] = result.error;
        return;
      }
      this.answered = result.session;
      this.liveRequests = this.liveRequests.filter(request => request.sessionId !== session.sessionId);
    });
  }

  /**
   * ITP-E-177-04 — the answered chat moved: disconnected (from here, the ticket or the client) or a later session of its
   * ticket. Once it is Closed this member's Answer buttons come back.
   */
  onAnsweredChange (session: LiveSupportSessionCard) {
    if (!this.answered || !session) {
      return;
    }
    if (session.sessionId === this.answered.sessionId || isLaterSessionOfTicket(this.answered, session)) {
      this.answered = session;
    }
  }

  /** ITP-E-177-04 — ends the answered live chat; the ticket and its assignment stay. */
  disconnectAnswered () {
    if (!this.inLiveChat || this.disconnecting) {
      return;
    }
    this.disconnecting = true;
    this.liveSupportService.disconnect(this.answered.sessionId).subscribe(result => {
      this.disconnecting = false;
      this.onAnsweredChange(result.session);
    });
  }

  /** ITP-E-177-03 — one session per member: while this member's answered chat is Live, every Answer is off. */
  get inLiveChat (): boolean {
    return this.answered?.state === 'Live';
  }

  canAnswer (session: LiveSupportSessionCard): boolean {
    return !this.answering[session.sessionId] && !this.taken[session.sessionId] && !this.inLiveChat;
  }

  /** Why a row's Answer is off, as its tooltip. */
  answerHint (session: LiveSupportSessionCard): string {
    if (this.inLiveChat) {
      return `You're in a live chat (${this.answered.ticketDisplayId})`;
    }
    return this.taken[session.sessionId] ? this.answerErrors[session.sessionId] : '';
  }

  /** ITP-E-233-06 — the "Raised from a demo" chip's tooltip: the demo and its package configuration. */
  demoTooltip (session: LiveSupportSessionCard): string {
    return demoChipTooltip(session?.demoContext);
  }

  /** How long a request has waited, as m:ss. */
  waitingFor (session: LiveSupportSessionCard): string {
    const since = new Date(session.requestedAt).getTime();
    if (isNaN(since)) {
      return '';
    }
    const seconds = Math.max(0, Math.floor((Date.now() - since) / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }

  // The waiting list re-reads itself until the support-live channel arrives (ITP-E-178).
  private followLiveRequests () {
    interval(WaitingSupportConversationListComponent.LiveRequestsRefreshMs).pipe(
      startWith(0),
      switchMap(() => this.liveSupportService.listWaiting()),
      takeUntil(this.destroy$)
    ).subscribe(requests => this.liveRequests = this.withTaken(requests));
  }

  /** The waiting requests, plus the ones someone else just answered — kept TakenShownMs so the row can say who. */
  private withTaken (requests: LiveSupportSessionCard[]): LiveSupportSessionCard[] {
    const now = Date.now();
    Object.keys(this.taken)
      .filter(id => now - this.taken[id].at >= WaitingSupportConversationListComponent.TakenShownMs)
      .forEach(id => {
        delete this.taken[id];
        delete this.answerErrors[id];
      });
    const listed = new Set(requests.map(request => request.sessionId));
    const kept = Object.values(this.taken).map(entry => entry.request).filter(request => !listed.has(request.sessionId));
    return [...requests, ...kept];
  }

  getConversationList () {
    this.collaborationStore.dispatch(new GetConversationListAction(CollaborationConfig.CollaborationApiKey,
      CollaborationQuery.getWaitingSupportConversationList()));
  }

  subscribeToConversationList () {
    this.conversationList$ = this.collaborationStore.select(getConversationList);
    this.conversationList$.subscribe(conversationList => {
      this.conversationList = conversationList;
    });
  }

  navigateToConversation(conversation) {
    this.viewSynchronizerService.handleMessageAction('',{});

    const sendMessageCommand = new PickupLiveSupportConversationCommand(conversation.Id);
    sendMessageCommand.scope = conversation.scope;
    this.collaborationStore.dispatch(new PickupLiveSupportConversationAction(sendMessageCommand, ConversationComponentName));
  }

}
